import { editsDocumentsIn, type SpaceChoice, useSpace } from '../../lib/org'

/** One space a Studio shelf shows, and whether the person changes what it holds. */
export type ShelfSpace = {
  spaceId: string
  /** How the shelf names it: the org's name, or "Yours" for the person's own. */
  name: string
  personal: boolean
  edits: boolean
}

const shelfSpace = (s: SpaceChoice): ShelfSpace => ({
  spaceId: s.spaceId,
  name: s.kind === 'personal' ? 'Yours' : s.name,
  personal: s.kind === 'personal',
  edits: editsDocumentsIn(s),
})

/**
 * The spaces the shelf gathers from the space Studio is in. A person's own
 * space gathers every space Studio works in for them — their own first, then
 * each org whose documents they read — and an org's space gathers itself
 * alone.
 */
export function shelfOf(
  spaces: readonly SpaceChoice[],
  spaceId: string | null,
): ShelfSpace[] {
  const current = spaces.find((s) => s.spaceId === spaceId)
  if (!current) return []
  if (current.kind !== 'personal') return [shelfSpace(current)]
  return [
    shelfSpace(current),
    ...spaces.filter((s) => s.kind === 'org').map(shelfSpace),
  ]
}

/**
 * The shelf on screen. Read off `space.mine`, which lists the spaces the
 * person's own roles and grants reach, never every space an operator could
 * open: reaching somebody else's shelf stays a deliberate act, audited where
 * it is made.
 */
export function useShelf(): ShelfSpace[] {
  const { spaces, spaceId } = useSpace()
  return shelfOf(spaces, spaceId)
}

/** What one view of the shelf reads, files in and creates in. */
export type ShelfScope = {
  /** The space whose folders the view shows: the one named, else Studio's. */
  folderSpace: string | null
  /** The spaces whose documents the view lists. */
  listed: ShelfSpace[]
  /** Where a new book or deck may go, the person's own space first. */
  createIn: ShelfSpace[]
  /** Whether the person changes the folder space's documents. */
  edits: boolean
}

/**
 * A view gathering the shelf (All documents, Recent) lists every space of it
 * and creates in any the person edits, unless the address names one space.
 * Every other view — the folders, Unfiled, Shared by you — is one space's: the
 * one named, else the one Studio is in. A named space that is not on the shelf
 * names nothing.
 */
export function shelfScope(
  shelf: readonly ShelfSpace[],
  {
    named,
    spaceId,
    gathers,
  }: {
    named: string
    spaceId: string | null
    gathers: boolean
  },
): ShelfScope {
  const one = shelf.find((s) => s.spaceId === named) ?? null
  const folderSpace = one?.spaceId ?? spaceId
  const listed = one
    ? [one]
    : gathers
      ? [...shelf]
      : shelf.filter((s) => s.spaceId === folderSpace)
  return {
    folderSpace,
    listed,
    createIn: listed.filter((s) => s.edits),
    edits: shelf.some((s) => s.spaceId === folderSpace && s.edits),
  }
}
